import { useState } from 'react'
import { Clock, Loader2, Mail, MailOpen, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { format, formatDistanceToNow } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { useCancelInvitation, type AgentInvitation } from '@/api/team'

interface Props {
  invitations: AgentInvitation[]
}

export function InvitationsList({ invitations }: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocale = locale === 'fr' ? fr : enUS
  const cancelInvitation = useCancelInvitation()
  const [pending, setPending] = useState<AgentInvitation | null>(null)

  if (invitations.length === 0) {
    return (
      <div className="text-center py-20">
        <MailOpen className="h-12 w-12 text-slate-200 dark:text-slate-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
          {t('manager.team.invitations.emptyTitle')}
        </h3>
        <p className="text-sm text-slate-400 dark:text-slate-500 max-w-sm mx-auto">
          {t('manager.team.invitations.emptyDescription')}
        </p>
      </div>
    )
  }

  const handleConfirm = async () => {
    if (!pending) return
    try {
      await cancelInvitation.mutateAsync(pending.id)
      toast.success(t('manager.team.invitations.cancelSuccess'))
    } catch {
      toast.error(t('manager.team.invitations.cancelError'))
    } finally {
      setPending(null)
    }
  }

  return (
    <div className="space-y-1">
      {invitations.map((inv) => {
        const expiresIn = formatDistanceToNow(new Date(inv.expires_at), {
          locale: dateLocale,
          addSuffix: true,
        })
        const sentOn = format(new Date(inv.created_at), 'PPP', {
          locale: dateLocale,
        })

        return (
          <div
            key={inv.id}
            className="group flex items-center justify-between py-4 px-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-xl transition-colors"
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-11 h-11 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 flex items-center justify-center flex-shrink-0">
                <Mail className="h-5 w-5" />
              </div>
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-3">
                  <h3 className="text-[14px] font-medium text-slate-900 dark:text-white truncate">
                    {inv.email}
                  </h3>
                  <Badge
                    className={
                      inv.is_expired
                        ? 'bg-red-50 text-red-600 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30 border text-[10px] font-semibold uppercase tracking-wide'
                        : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30 border text-[10px] font-semibold uppercase tracking-wide'
                    }
                  >
                    {inv.is_expired
                      ? t('manager.team.invitations.statusExpired')
                      : t('manager.team.invitations.statusPending')}
                  </Badge>
                </div>
                <p className="text-[13px] text-slate-400 dark:text-slate-500 flex items-center gap-1 truncate">
                  <Clock className="h-3 w-3 flex-shrink-0" />
                  {t('manager.team.invitations.sentOn', { date: sentOn })}
                  {' · '}
                  {inv.is_expired
                    ? t('manager.team.invitations.expired')
                    : t('manager.team.invitations.expiresIn', {
                        time: expiresIn,
                      })}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPending(inv)}
              title={t('manager.team.invitations.cancel')}
              className="p-2 text-slate-300 dark:text-slate-600 hover:text-red-500 transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )
      })}

      <AlertDialog
        open={!!pending}
        onOpenChange={(open) => !open && setPending(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('manager.team.invitations.cancelTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending &&
                t('manager.team.invitations.cancelDescription', {
                  email: pending.email,
                })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelInvitation.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirm()
              }}
              disabled={cancelInvitation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {cancelInvitation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('manager.team.invitations.confirmCancel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
